import { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useFund } from '../context/FundContext';
import FundDateBar from '../components/FundDateBar';
import { Loading } from '../components/Loader';
import { api } from '../api';
import { money, shortName } from '../format';

const COLORS = ['#3fb6c9', '#5b6ee1', '#bb6bd9', '#4caf7d', '#f2994a', '#e57373', '#9575cd', '#90a4ae'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2025-08" -> "Aug'25"
function monthLabel(ym) {
  const [y, m] = String(ym).split('-');
  return `${MONTHS[Number(m) - 1]}'${y.slice(2)}`;
}
function pct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(1)}%`;
}

export default function Performance() {
  const { fund, date } = useFund();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fund || !date) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    api.performanceDashboard(fund, date)
      .then((data) => { if (active) { setD(data); setError(null); } })
      .catch((e) => { if (active) { setError(e.message); setD(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fund, date]);

  return (
    <div>
      <FundDateBar />
      {error && <div className="error">Error: {error}</div>}
      {loading && <Loading label="Loading performance…" />}

      {d && !loading && (!d.hasData ? (
        <div className="panel"><p className="muted">No performance data for this fund as of {date}.</p></div>
      ) : (
        <>
          <div className="perf-top">
            <div className="panel">
              <h3>Gain/Loss Trend Over Time — Monthly Figures (USD)</h3>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={d.monthlyTrend.map((m) => ({ month: monthLabel(m.month), v: m.netPerf }))}
                  margin={{ top: 10, right: 20, left: 0, bottom: 4 }}>
                  <XAxis dataKey="month" fontSize={11} minTickGap={10} />
                  <YAxis fontSize={11} width={60} tickFormatter={(v) => money(v)} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Line type="monotone" dataKey="v" stroke="#f2704a" strokeWidth={2.5}
                    dot={{ r: 3, fill: '#f2704a' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="perf-side">
              <div className="panel perf-highlight">
                <div className="perf-highlight-title">Highest Performance Over Time (USD)</div>
                {d.highest ? (
                  <>
                    <div className="perf-highlight-value">{money(d.highest.netPerf)}</div>
                    <div className="perf-highlight-sub">{monthLabel(d.highest.month)}</div>
                  </>
                ) : <div className="muted">—</div>}
              </div>

              <div className="panel">
                <h3>Profit Margin Analysis</h3>
                <div className="muted-inline">Latest quarter · derived from performance series</div>
                <div className="perf-margins">
                  <div><div className="perf-margin-value">{pct(d.profitMargin.feesPct)}</div><div className="perf-margin-label">Fees</div></div>
                  <div><div className="perf-margin-value">{pct(d.profitMargin.netMarginPct)}</div><div className="perf-margin-label">Net Profit Margin</div></div>
                </div>
              </div>
            </div>
          </div>

          <div className="bottom-grid perf-charts">
            <div className="panel">
              <h3>Net P&amp;L by Asset Class (USD)</h3>
              {d.netPnlByAsset.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={d.netPnlByAsset.map((a) => ({ name: a.assetClass, v: a.pnl }))}
                    margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis fontSize={11} width={60} tickFormatter={(v) => money(v)} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Bar dataKey="v" radius={[3, 3, 0, 0]}>
                      {d.netPnlByAsset.map((a, i) => <Cell key={i} fill={a.pnl >= 0 ? '#4caf7d' : '#e57373'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="muted">No positions for this date.</p>}
            </div>

            <div className="panel">
              <h3>Expenses Breakdown by Category</h3>
              {d.expensesBreakdown.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={d.expensesBreakdown.map((e) => ({ ...e, v: e.amount }))}
                      dataKey="v" nameKey="category" outerRadius={95} label={(e) => shortName(e.category)}>
                      {d.expensesBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="muted">No expenses recorded.</p>}
            </div>

            <div className="panel">
              <h3>Exposure By Asset Class</h3>
              {d.exposureByAsset.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={d.exposureByAsset.map((e) => ({ ...e, v: Math.abs(e.exposure) }))}
                      dataKey="v" nameKey="assetClass" outerRadius={95} label={(e) => e.assetClass}>
                      {d.exposureByAsset.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="muted">No positions for this date.</p>}
            </div>
          </div>
        </>
      ))}
    </div>
  );
}
